import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { useTenant } from '@/contexts/TenantContext';

export interface ProjectApiKey {
  id: string;
  name: string;
  apiKey?: string; // Plný klíč — jen při vytvoření
  apiKeyHint?: string; // Maskovaný klíč — v seznamu
  projectId: string;
  project: { id: string; name: string };
  isActive: boolean;
  lastUsedAt: string | null;
  createdAt: string;
}

export function useProjectApiKeys(projectId?: string) {
  const { currentTenant } = useTenant();

  const params = new URLSearchParams();
  if (projectId) params.set('projectId', projectId);
  const queryString = params.toString();

  return useQuery({
    queryKey: ['api-keys', projectId, currentTenant?.id],
    queryFn: () => api.get<ProjectApiKey[]>(`/api-keys${queryString ? `?${queryString}` : ''}`),
    enabled: !!currentTenant,
  });
}

export function useCreateApiKey() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: { projectId: string; name: string }) =>
      api.post<ProjectApiKey>('/api-keys', data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['api-keys'] });
    },
  });
}

export function useToggleApiKey() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) =>
      api.patch<ProjectApiKey>(`/api-keys/${id}/toggle`, {}),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['api-keys'] });
    },
  });
}

export function useDeleteApiKey() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) =>
      api.delete<{ success: boolean }>(`/api-keys/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['api-keys'] });
    },
  });
}
